import { useCallback, useEffect, useState } from 'react';
import { adminApi as defaultAdminApi } from './admin-api';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { generateTemporaryPassword, safeGenerateTemporaryPassword } from '../lib/temporary-password';
import { CoachForm } from './CoachForm';
import { TemporaryPasswordNotice } from './TemporaryPasswordNotice';
import { useFocusTrap } from '../components/useFocusTrap';
import './admin.css';

const MIN_PASSWORD_LENGTH = 8;

function CoachSheet({ children }) {
  const sheetRef = useFocusTrap(true);
  return <div className="gym-admin-sheet-backdrop"><div ref={sheetRef} tabIndex={-1} className="gym-card gym-admin-sheet">{children}</div></div>;
}

export function CoachesPage({ api = defaultAdminApi, generatePassword = generateTemporaryPassword }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', coaches: [], message: '' });
  const [form, setForm] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const confirmRef = useFocusTrap(Boolean(confirm));

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', coaches: [], message: '' });
    api.listCoaches().then(({ coaches }) => {
      if (active) setState({ status: 'ready', coaches: coaches || [], message: '' });
    }).catch(error => {
      if (!active) return;
      const outcome = handleApiError(error);
      if (!outcome.handled) setState({ status: 'error', coaches: [], message: errorMessageFor(error) });
    });
    return () => { active = false; };
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  function created(result) {
    setNotice({ username: result.coach.username, password: result.password });
    setForm(null);
    setState(prev => ({ ...prev, coaches: [result.coach, ...prev.coaches] }));
  }

  async function confirmAction() {
    if (!confirm || busy) return;
    if (confirm.type === 'reset' && confirm.generatedPassword.length < MIN_PASSWORD_LENGTH) {
      setActionError('La contraseña temporal debe tener al menos 8 caracteres.');
      return;
    }
    setBusy(true);
    setActionError('');
    try {
      if (confirm.type === 'deactivate') {
        await api.updateCoach(confirm.coach.id, { isActive: false });
      } else if (confirm.type === 'community-suspension') {
        await api.updateCoach(confirm.coach.id, { communitySuspended: !confirm.coach.communitySuspended });
      } else {
        await api.resetCoachPassword(confirm.coach.id, confirm.generatedPassword);
        setNotice({ username: confirm.coach.username, password: confirm.generatedPassword });
      }
      setConfirm(null);
      load();
    } catch (error) {
      const outcome = handleApiError(error);
      if (!outcome.handled) setActionError(errorMessageFor(error));
    } finally { setBusy(false); }
  }

  // Keep the reset secret available across the request without persisting it.
  function askReset(coach) {
    const passwordState = safeGenerateTemporaryPassword(generatePassword);
    setConfirm({
      type: 'reset',
      coach,
      generatedPassword: passwordState.password,
      generatedPasswordError: passwordState.error,
    });
  }

  function regenerateResetPassword() {
    const passwordState = safeGenerateTemporaryPassword(generatePassword);
    setConfirm(prev => ({
      ...prev,
      generatedPassword: passwordState.password,
      generatedPasswordError: passwordState.error,
    }));
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-admin-coaches__header">
        <h1 id="gym-page-title" className="gym-page__title">Profesores</h1>
        {state.status === 'ready' && !form && !notice && <button type="button" className="gym-button gym-button--primary" onClick={() => setForm({ type: 'create' })}>Nuevo profesor</button>}
      </header>
      {notice && <TemporaryPasswordNotice username={notice.username} password={notice.password} onClose={() => setNotice(null)} />}
      {form && <CoachSheet><CoachForm key={form.coach?.id || 'create'} api={api} coach={form.coach} generatePassword={generatePassword} onCreated={created} onSaved={() => { setForm(null); load(); }} onCancel={() => setForm(null)} /></CoachSheet>}
      {actionError && !confirm && <p role="alert" className="gym-form__error">{actionError}</p>}
      {state.status === 'loading' && <div role="status" className="gym-state gym-state--loading"><p className="gym-state__text">Cargando…</p></div>}
      {state.status === 'error' && <div role="alert" className="gym-state gym-state--error"><p className="gym-state__text">{state.message}</p><button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button></div>}
      {state.status === 'ready' && !state.coaches.length && <div className="gym-state gym-state--empty"><p className="gym-state__text">Todavía no hay profesores cargados.</p></div>}
      {state.status === 'ready' && state.coaches.length > 0 && <div className="gym-admin-coaches__list">
        {state.coaches.map(coach => <article className="gym-card gym-admin-coach-card" key={coach.id}>
          <div className="gym-admin-coach-card__identity"><span aria-hidden="true" className="gym-admin-coach-card__initial">{coach.username.trim().charAt(0).toUpperCase()}</span><div className="gym-admin-coach-card__details"><h2>{coach.username}</h2><span className={coach.isActive ? 'gym-badge gym-badge--success' : 'gym-badge gym-badge--neutral'}>{coach.isActive ? 'Activo' : 'Inactivo'}</span><p>{coach.email}</p><p className="gym-muted-text">{coach.assignedMembersCount} socios asignados</p></div></div>
          <div className="gym-admin-coach-card__actions gym-admin-coach-card__actions--grid">
            <button type="button" aria-label={`Editar profesor ${coach.username}`} className="gym-button gym-button--secondary" onClick={() => setForm({ type: 'edit', coach })}>Editar</button>
            <button type="button" aria-label={`Restablecer contraseña profesor ${coach.username}`} className="gym-button gym-button--danger-outline" onClick={() => askReset(coach)}>Restablecer contraseña</button>
            <button type="button" aria-label={`${coach.communitySuspended ? 'Quitar suspensión de' : 'Suspender a'} ${coach.username} en Comunidad`} className="gym-button gym-button--danger-outline" onClick={() => setConfirm({ type: 'community-suspension', coach })}>
              {coach.communitySuspended ? 'Rehabilitar en Comunidad' : 'Suspender en Comunidad'}
            </button>
            {coach.isActive && <button type="button" aria-label={`Desactivar profesor ${coach.username}`} className="gym-button gym-button--danger-outline" onClick={() => setConfirm({ type: 'deactivate', coach })}>Desactivar</button>}
          </div>
        </article>)}
      </div>}
      {confirm && <div className="gym-plans-confirm-backdrop gym-admin-coach-confirm-backdrop"><section ref={confirmRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={confirm.type === 'deactivate' ? 'Desactivar profesor' : confirm.type === 'community-suspension' ? 'Suspensión en Comunidad' : 'Restablecer contraseña'} className="gym-card gym-plans-confirm gym-admin-sheet">
        <h2>{confirm.type === 'deactivate' ? `¿Desactivar a ${confirm.coach.username}?` : confirm.type === 'community-suspension' ? `${confirm.coach.communitySuspended ? '¿Quitar la suspensión de' : '¿Suspender a'} ${confirm.coach.username} en Comunidad?` : `¿Restablecer la contraseña de ${confirm.coach.username}?`}</h2>
        <p>{confirm.type === 'deactivate' ? 'El profesor perderá acceso. Sus asignaciones se conservarán.' : confirm.type === 'community-suspension' ? 'La persona no podrá publicar, comentar ni reaccionar mientras siga suspendida.' : 'Se revocará la sesión actual y el profesor deberá cambiar la clave.'}</p>
        {actionError && <p role="alert" className="gym-form__error">{actionError}</p>}
        {confirm.type === 'reset' && <>
          {confirm.generatedPasswordError && <p role="alert" className="gym-form__error">{confirm.generatedPasswordError}</p>}
          <div className="gym-field">
            <label htmlFor="coach-reset-password" className="gym-field__label">Contraseña temporal</label>
            <div className="gym-admin-password-row">
              <input
                id="coach-reset-password"
                name="temporaryPassword"
                type="text"
                autoComplete="off"
                spellCheck="false"
                required
                value={confirm.generatedPassword}
                disabled={busy}
                onChange={event => setConfirm(prev => ({
                  ...prev,
                  generatedPassword: event.target.value,
                  generatedPasswordError: '',
                }))}
                className="gym-input gym-admin-password-row__input"
              />
              <button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={regenerateResetPassword}>Regenerar</button>
            </div>
          </div>
        </>}
        <div className="gym-admin-form-actions"><button type="button" className="gym-button gym-button--danger" disabled={busy} onClick={confirmAction}>{confirm.type === 'deactivate' ? 'Desactivar' : confirm.type === 'community-suspension' ? 'Confirmar suspensión' : 'Confirmar restablecimiento'}</button><button type="button" className="gym-button gym-button--secondary" disabled={busy} onClick={() => setConfirm(null)}>Cancelar</button></div>
      </section></div>}
    </section>
  );
}
